import styled, { css, keyframes } from "styled-components";

const ACCENT   = "#3f88a5";
const ACCENT_2 = "#e0f2fe";
const ACCENT_3 = "#f0f9ff";

export { ACCENT, ACCENT_2, ACCENT_3 };

// ─── Skeleton animation ───────────────────────────────────────

export const shimmer = keyframes`
  0%   { background-position: -600px 0; }
  100% { background-position:  600px 0; }
`;

export const SkeletonCell = styled.div`
  height: 14px;
  border-radius: 6px;
  width: ${({ $w }) => $w ?? "80%"};
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 600px 100%;
  animation: ${shimmer} 1.4s infinite linear;
`;

// ─── Shell ────────────────────────────────────────────────────

export const Shell = styled.div`
  background: var(--surface, #fff);
  border: 0.5px solid var(--rule, rgba(0,0,0,0.1));
  border-radius: 12px;
  overflow: hidden;
`;

// ─── Toolbar ──────────────────────────────────────────────────

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 0.5px solid var(--rule, rgba(0,0,0,0.08));
  gap: 12px;
  flex-wrap: wrap;
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const TableTitle = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: var(--ink, #111);
  font-family: var(--font-sans);
`;

export const CountChip = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${ACCENT_2};
  color: ${ACCENT};
  border-radius: 20px;
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
`;

export const SelectionChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${ACCENT};
  color: #fff;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 500;
  padding: 3px 10px;

  button {
    background: transparent;
    border: none;
    color: #fff;
    cursor: pointer;
    padding: 0;
    line-height: 1;
    font-size: 11px;
    opacity: 0.8;
    &:hover { opacity: 1; }
  }
`;

export const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  border: 0.5px solid var(--rule, rgba(0,0,0,0.15));
  border-radius: 7px;
  padding: 6px 10px;
  background: var(--surface-2, #fafaf9);
  transition: border-color 0.15s, box-shadow 0.15s;

  &:focus-within {
    border-color: ${ACCENT};
    box-shadow: 0 0 0 3px rgba(63,136,165,0.12);
  }

  input {
    border: none;
    outline: none;
    background: transparent;
    font-size: 12.5px;
    width: 160px;
    color: var(--ink, #111);
    font-family: inherit;
    &::placeholder { color: var(--ink-3, #9ca3af); }
  }

  i { font-size: 13px; color: var(--ink-3, #9ca3af); }
`;

export const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 7px 13px;
  border-radius: 7px;
  border: none;
  background: ${ACCENT};
  color: #fff;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  transition: opacity 0.15s;
  &:hover { opacity: 0.88; }
  i { font-size: 13px; }
`;

// ─── Table ────────────────────────────────────────────────────

export const TableWrap = styled.div`
  overflow-x: auto;
  scrollbar-width: thin;
  /* Virtual scroll container */
  ${({ $virtual }) => $virtual && css`
    overflow-y: auto;
    max-height: ${({ $maxHeight }) => $maxHeight ?? "480px"};
  `}
`;

export const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
`;

export const Th = styled.th`
  padding: 10px 14px;
  font-size: 11.5px;
  font-weight: 600;
  /* ── ACCENT header color ── */
  color: ${ACCENT};
  text-align: ${({ $right }) => ($right ? "right" : "left")};
  white-space: nowrap;
  background: ${ACCENT_3};
  border-bottom: 1.5px solid ${ACCENT_2};
  user-select: none;
  cursor: ${({ $sortable }) => ($sortable ? "pointer" : "default")};
  position: relative;

  &:hover {
    background: ${({ $sortable }) => ($sortable ? ACCENT_2 : ACCENT_3)};
  }

  i.sort-icon {
    font-size: 11px;
    vertical-align: -1px;
    margin-left: 4px;
    opacity: 0.6;
  }

  /* drag handle */
  &::after {
    content: "";
    position: absolute;
    right: 0;
    top: 20%;
    height: 60%;
    width: 1px;
    background: ${ACCENT_2};
  }
  &:last-child::after { display: none; }
`;

export const ThInner = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: ${({ $right }) => ($right ? "flex-end" : "flex-start")};
`;

export const ReorderBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${ACCENT};
  opacity: 0;
  padding: 0;
  border-radius: 3px;
  transition: opacity 0.12s, background 0.12s;
  font-size: 10px;

  ${Th}:hover & { opacity: 1; }
  &:hover { background: ${ACCENT_2}; opacity: 1; }
  &:disabled { opacity: 0.2; cursor: not-allowed; }
`;

export const Tr = styled.tr`
  border-bottom: 0.5px solid var(--rule, rgba(0,0,0,0.06));
  transition: background 0.1s;

  &:last-child { border-bottom: none; }
  &:hover { background: ${ACCENT_3}; }

  ${({ $editing  }) => $editing   && css`background: ${ACCENT_2} !important;`}
  ${({ $new      }) => $new       && css`background: #f0fdf4 !important;`}
  ${({ $selected }) => $selected  && css`
    background: ${ACCENT_2} !important;
    &:hover { background: #bfdbfe !important; }
  `}
`;

export const Td = styled.td`
  padding: 9px 14px;
  font-size: 13px;
  color: var(--ink, #111);
  vertical-align: middle;
  text-align: ${({ $right }) => ($right ? "right" : "left")};
`;

export const CellText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
`;

export const CellInput = styled.input`
  width: 100%;
  border: 1.5px solid ${ACCENT};
  border-radius: 6px;
  padding: 5px 8px;
  font-size: 12.5px;
  font-family: inherit;
  color: var(--ink, #111);
  background: var(--surface, #fff);
  outline: none;
  transition: box-shadow 0.15s;
  &:focus { box-shadow: 0 0 0 3px rgba(63,136,165,0.15); }
`;

export const CellSelect = styled.select`
  width: 100%;
  border: 1.5px solid ${ACCENT};
  border-radius: 6px;
  padding: 5px 8px;
  font-size: 12.5px;
  font-family: inherit;
  color: var(--ink, #111);
  background: var(--surface, #fff);
  outline: none;
  cursor: pointer;
`;

export const CheckboxCell = styled.td`
  padding: 9px 0 9px 14px;
  width: 36px;
  vertical-align: middle;
`;

export const CheckboxTh = styled.th`
  padding: 10px 0 10px 14px;
  width: 36px;
  background: ${ACCENT_3};
  border-bottom: 1.5px solid ${ACCENT_2};
`;

export const StyledCheckbox = styled.input.attrs({ type: "checkbox" })`
  width: 15px;
  height: 15px;
  accent-color: ${ACCENT};
  cursor: pointer;
`;

export const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  justify-content: flex-end;
`;

export const ActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 29px;
  height: 29px;
  border-radius: 6px;
  border: 0.5px solid transparent;
  background: transparent;
  cursor: pointer;
  color: var(--ink-2, #6b7280);
  transition: background 0.12s, border-color 0.12s, color 0.12s;
  padding: 0;

  i { font-size: 14px; }
  &:hover { border-color: rgba(0,0,0,0.12); }
  &:focus-visible {
    outline: 2px solid ${ACCENT};
    outline-offset: 1px;
  }

  ${({ $variant }) => $variant === "view"   && css`&:hover{background:${ACCENT_2};color:${ACCENT}}`}
  ${({ $variant }) => $variant === "edit"   && css`&:hover{background:#fef9c3;color:#854d0e}`}
  ${({ $variant }) => $variant === "save"   && css`&:hover{background:#dcfce7;color:#166534}`}
  ${({ $variant }) => $variant === "del"    && css`&:hover{background:#fee2e2;color:#991b1b}`}
  ${({ $variant }) => $variant === "cancel" && css`&:hover{background:#f3f4f6;color:#374151}`}
`;

// ─── Footer ───────────────────────────────────────────────────

export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-top: 0.5px solid var(--rule, rgba(0,0,0,0.07));
  font-size: 12px;
  color: var(--ink-2, #6b7280);
  flex-wrap: wrap;
  gap: 8px;
`;

export const PgBtns = styled.div`
  display: flex;
  gap: 4px;
`;

export const PgBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 0.5px solid ${({ $active }) => ($active ? ACCENT : "rgba(0,0,0,0.12)")};
  background: ${({ $active }) => ($active ? ACCENT : "transparent")};
  color: ${({ $active }) => ($active ? "#fff" : "var(--ink, #111)")};
  font-size: 12px;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.12s;
  &:hover:not(:disabled) {
    background: ${({ $active }) => ($active ? ACCENT : "var(--surface-2,#fafaf9)")};
  }
  &:focus-visible { outline: 2px solid ${ACCENT}; outline-offset: 1px; }
  i { font-size: 12px; }
`;

// ─── Empty / loading ──────────────────────────────────────────

export const EmptyState = styled.div`
  padding: 40px;
  text-align: center;
  color: var(--ink-3, #9ca3af);
  font-size: 13px;
  i { font-size: 28px; display: block; margin-bottom: 8px; color: #d1d5db; }
`;

export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 20px;
  font-size: 11.5px;
  font-weight: 500;
  white-space: nowrap;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
`;